/**
 * SmartDailyDescriptionInput
 * =============================================================================
 * Campo de descrição do Smart Daily com sugestão de abreviação.
 *
 * POR QUE NÃO É UM <Autocomplete> NORMAL
 *   O valor não é um item só: é uma expressão tipo "1 CB + 2 CH". Um Autocomplete
 *   comum substituiria a linha inteira. Aqui a sugestão age só no ÚLTIMO termo
 *   (o que vem depois do último "+"), que é o que a pessoa está digitando.
 *
 * Isso ataca direto o erro "Unknown item" — a causa quase sempre é abreviação
 * digitada errado.
 */

import React, { useMemo, useRef, useState } from 'react';
import { TextField, Paper, MenuList, MenuItem, Popper, ClickAwayListener, Typography, Box } from '@mui/material';

const MAX_SUGGESTIONS = 6;

// "1 CB + 2 CH" -> { prefix: "1 CB + 2 ", token: "CH" }
// Mantém a quantidade colada no termo, então só o texto do serviço é trocado.
export const splitLastToken = (value) => {
  const text = value || '';
  const lastPlus = text.lastIndexOf('+');
  const tail = text.slice(lastPlus + 1);
  const match = tail.match(/([A-Za-zÀ-ÖØ-öø-ÿ][\w'/-]*)\s*$/);
  if (!match) return { prefix: text, token: '' };
  const tokenStart = lastPlus + 1 + match.index;
  return { prefix: text.slice(0, tokenStart), token: match[1] };
};

export default function SmartDailyDescriptionInput({
  value,
  onChange,
  services = [],
  ...textFieldProps
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const { token } = useMemo(() => splitLastToken(value), [value]);

  const suggestions = useMemo(() => {
    const q = token.trim().toLowerCase();
    if (q.length < 1 || services.length === 0) return [];
    const starts = [];
    const contains = [];
    for (const s of services) {
      const abbr = String(s.abbreviation || '').toLowerCase();
      const name = String(s.name || '').toLowerCase();
      if (abbr === q) continue;                       // já digitou exato
      if (abbr.startsWith(q)) starts.push(s);
      else if (abbr.includes(q) || name.includes(q)) contains.push(s);
    }
    return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [token, services]);

  const applySuggestion = (service) => {
    const { prefix } = splitLastToken(value);
    onChange(`${prefix}${service.abbreviation} `);
    setOpen(false);
  };

  const showPopper = open && suggestions.length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <TextField
          {...textFieldProps}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // Tab/Enter aceitam a primeira sugestão — digitação sem tirar a mão do teclado
            if (showPopper && (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey))) {
              e.preventDefault();
              applySuggestion(suggestions[0]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />

        <Popper
          open={showPopper}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth }}
        >
          <Paper elevation={4} sx={{ maxHeight: 240, overflowY: 'auto' }}>
            <MenuList dense>
              {suggestions.map((s) => (
                <MenuItem key={s.abbreviation} onClick={() => applySuggestion(s)}>
                  <Typography component="span" sx={{ fontWeight: 700, mr: 1 }}>
                    {s.abbreviation}
                  </Typography>
                  <Typography component="span" variant="caption" color="text.secondary" noWrap>
                    {s.name}
                  </Typography>
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
